'use client'

import { useEffect, useRef } from 'react'
import {
  BottomSheet,
  Button,
  Cell,
  Container,
  Divider,
  Dialog,
  Navigation,
  Spacer,
  Text,
} from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'

import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  clickEvent,
  EscrowFeesModel,
  getItemEscrowFees,
  transformItemEscrowFeesResponse,
} from '@marketplace-web/escrow-pricing/escrow-pricing-data'
import { useAbTest, useTrackAbTest } from '@marketplace-web/feature-flags/ab-tests-data'

import { observeEscrowFeesLoadTime } from '../../utils/observability'
import type { FlowType } from '../../utils/observability'
import ItemPricingDetailsIcon from '../ItemPricingDetailsIcon'
import { AdditionalNote } from '../DetailedItemPriceBreakdownModal/AdditionalNote'

type Props = {
  itemId: number
  isVisible: boolean
  isSellerBusiness: boolean
  itemPrice: string
  serviceFee: string | null
  onClose: () => void
  trackUnification?: boolean
  escrowFees?: EscrowFeesModel
}

const ItemPriceBreakdownModal = ({
  isVisible,
  itemId,
  itemPrice,
  isSellerBusiness,
  serviceFee,
  onClose,
  trackUnification = false,
  escrowFees: providedEscrowFees,
}: Props) => {
  const translate = useTranslate('item_price_breakdown_modal')
  const unifiedPriceBreakdownIsAbTest = useAbTest('unified_price_breakdown_v2')
  const loadStartTime = useRef<number>(performance.now())

  const {
    transformedData: itemEscrowFees,
    fetch: fetchItemEscrowFees,
    isLoading: isItemEscrowFeesLoading,
  } = useFetch(getItemEscrowFees, transformItemEscrowFeesResponse)

  useTrackAbTest(unifiedPriceBreakdownIsAbTest, trackUnification && isVisible)
  const breakpoint = useBreakpoint()

  const { track } = useTracking()

  const userTypeSuffix = isSellerBusiness ? '_pro' : ''

  useEffect(() => {
    if (providedEscrowFees || !isVisible) return

    const observeLoadEnd = ({
      isSuccess,
      flowType,
    }: {
      isSuccess: boolean
      flowType: FlowType
    }) => {
      const escrowFeesLoadingTime = performance.now() - loadStartTime.current

      observeEscrowFeesLoadTime({ isSuccess, flowType, loadTime: escrowFeesLoadingTime })
    }

    fetchItemEscrowFees({ itemId }).then(({ data }) => {
      observeLoadEnd({ isSuccess: !!data, flowType: 'item' })
    })
  }, [fetchItemEscrowFees, itemId, isVisible, providedEscrowFees])

  const handleClose = () => {
    track(
      clickEvent({
        screen: 'price_breakdown_modal',
        target: 'close_screen',
      }),
    )

    onClose()
  }

  const renderActionButton = () => (
    <Cell styling="narrow">
      <Button
        text={translate('actions.close')}
        styling="filled"
        onClick={handleClose}
        testId="item-price-breakdown-close-button"
      />
    </Cell>
  )

  const noteTranslationKey =
    providedEscrowFees?.noteTranslationKey ?? itemEscrowFees?.noteTranslationKey

  const renderLoadedContent = () => (
    <>
      <Container styling="narrow">
        <Cell
          title={translate('item_price')}
          suffix={itemPrice}
          styling="narrow"
          testId="item-price-breakdown-price-cell"
        />
        <Cell
          suffix={serviceFee}
          styling="narrow"
          body={
            <div className="u-flexbox">
              <Text
                as="span"
                text={translate(`buyer_protection_fee${userTypeSuffix}`)}
                theme="amplified"
                bold
              />
              <Spacer size="small" orientation="vertical" />
              <ItemPricingDetailsIcon itemId={itemId} isSellerBusiness={isSellerBusiness} />
            </div>
          }
          testId="item-price-breakdown-service-fee-cell"
        />
        <Cell styling="narrow">
          <Text
            as="h3"
            text={translate('shipping_info')}
            type="subtitle"
            testId="item-price-breakdown-shipping-note"
          />
        </Cell>
      </Container>

      <Divider />
      <Container styling="narrow">
        {noteTranslationKey && <AdditionalNote noteTranslationKey={noteTranslationKey} />}
        <Cell styling="narrow">
          <Text
            as="h4"
            testId="legal-note"
            type="caption"
            text={translate(`legal_note${userTypeSuffix}`)}
          />
        </Cell>
      </Container>
    </>
  )

  const renderContent = () => (
    <>
      <Navigation
        right={
          <Button
            styling="flat"
            iconName={X24}
            inline
            onClick={handleClose}
            aria={{ 'aria-label': translate('actions.a11y.close') }}
            testId="item-price-breakdown-navigation-close-button"
          />
        }
        body={translate('breakdown_title')}
      />
      <Divider />

      {isItemEscrowFeesLoading ? <ContentLoader /> : renderLoadedContent()}

      <div className="u-tablets-up-only">
        <Container styling="narrow">{renderActionButton()}</Container>
      </div>
    </>
  )

  if (breakpoint.tabletsUp) return <Dialog show={isVisible}>{renderContent()}</Dialog>

  return (
    <BottomSheet isVisible={isVisible} onClose={handleClose}>
      {renderContent()}
    </BottomSheet>
  )
}

export default ItemPriceBreakdownModal
